import React from 'react';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { isoWeek, relativeDue, shortDate } from './helpers';
import { cardHoverSx, cardSx, launchColors, launchStatusStyle, PillStyle } from './launchTheme';
import { LaunchSummary } from './types';
import PersonAvatar from './ui/PersonAvatar';
import ProgressRing from './ui/ProgressRing';
import { LaunchStatusPill, StatusPill } from './ui/StatusPill';

export const launchRisk = (launch: LaunchSummary): (PillStyle & { label: string }) | null => {
  if (launch.status === 'closed' || launch.status === 'cancelled') return null;
  if (launch.status === 'draft') return { label: 'Inte aktiverad', fg: launchColors.textSecondary, bg: launchColors.neutralBg };
  if (launch.activeBlockerCount > 0) return { label: `${launch.activeBlockerCount} blockerade`, fg: launchColors.dangerText, bg: launchColors.dangerBg };
  if (launch.overdueCount > 0) return { label: `${launch.overdueCount} försenade`, fg: launchColors.dangerText, bg: launchColors.dangerBg };
  if (launch.atRiskCount > 0) return { label: `${launch.atRiskCount} med risk`, fg: launchColors.plumDark, bg: launchColors.plumLight };
  if (launch.dueSoonCount > 0) return { label: `${launch.dueSoonCount} snart`, fg: launchColors.warningText, bg: launchColors.warningBg };
  return { label: 'I fas', fg: launchColors.mossDark, bg: launchColors.mossLight };
};

interface Props {
  launch: LaunchSummary;
  ownerName: string;
  asOf: string;
  onSelect: () => void;
}

const LaunchCard: React.FC<Props> = ({ launch, ownerName, asOf, onSelect }) => {
  const risk = launchRisk(launch);
  const products = launch.products.map((product) => product.name).join(', ') || 'Inga produkter';
  return (
    <ButtonBase
      component="div"
      onClick={onSelect}
      aria-label={`Öppna ${launch.name}`}
      sx={[cardSx, cardHoverSx, {
        display: 'block',
        textAlign: 'left',
        overflow: 'hidden',
        height: '100%',
        '&.Mui-focusVisible': { outline: `2px solid ${launchColors.navy}`, outlineOffset: 2 },
      }]}
    >
      <Box aria-hidden sx={{ height: 5, bgcolor: launchStatusStyle[launch.status].stripe }} />
      <Stack spacing={1.25} sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: launchColors.navy, lineHeight: 1.25, overflowWrap: 'anywhere' }}>{launch.name}</Typography>
            <Typography sx={{ fontSize: 12, color: launchColors.textSecondary, mt: 0.25 }}>{products}</Typography>
          </Box>
          <ProgressRing value={launch.progressPercent} size={56} />
        </Stack>
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
          <LaunchStatusPill status={launch.status} />
          {risk && <StatusPill label={risk.label} fg={risk.fg} bg={risk.bg} />}
        </Stack>
        <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>
          Nästa deadline{' '}
          <Box component="strong" sx={{ color: launchColors.text, fontVariantNumeric: 'tabular-nums' }}>
            {launch.nextDeadline ? `${shortDate(launch.nextDeadline, asOf)} · ${relativeDue(launch.nextDeadline, asOf)}` : 'saknas'}
          </Box>
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap">
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
            {(launch.chains || []).map((chain) => (
              <StatusPill key={chain.id} label={`${chain.name} · v.${isoWeek(chain.announcement)}`} fg={launchColors.petrolDark} bg={launchColors.petrolLight} />
            ))}
          </Stack>
          <PersonAvatar name={ownerName} size={22} unassigned={!launch.ownerUserId} />
        </Stack>
      </Stack>
    </ButtonBase>
  );
};

export default LaunchCard;
